import { useState } from "react";
import { toast } from "sonner";
import { Button } from "#app/components/ui/button.tsx";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { definitionOgImagePath } from "#app/utils/og/urls.ts";

interface ShareButtonProps {
	definitionId: string;
	word: string;
	slug: string;
	translation: string;
	size?: "sm" | "default";
}

export function ShareButton({
	definitionId,
	word,
	slug,
	translation,
	size = "sm",
}: ShareButtonProps) {
	const [downloading, setDownloading] = useState(false);

	const shareUrl = `https://shonaslang.com/words/${slug}`;
	const shareText = `${word}: ${translation}`;
	const ogImageUrl = definitionOgImagePath(definitionId);

	const handleNativeShare = async () => {
		try {
			await navigator.share({
				title: `${word} - shonaslang`,
				text: shareText,
				url: shareUrl,
			});
		} catch {
			// User cancelled share
		}
	};

	const handleCopyLink = async () => {
		await navigator.clipboard.writeText(shareUrl);
		toast.success("Link copied!");
	};

	const handleDownload = async () => {
		setDownloading(true);
		try {
			const res = await fetch(ogImageUrl);
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${word}-shonaslang.png`;
			a.click();
			URL.revokeObjectURL(url);
		} finally {
			setDownloading(false);
		}
	};

	// Mobile: use native share sheet
	if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
		return (
			<Button variant="outline" size={size} onClick={handleNativeShare}>
				<Icon name="link-2" size="sm" />
				Share
			</Button>
		);
	}

	// Desktop: dropdown with options
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size={size}>
					<Icon name="link-2" size="sm" />
					Share
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem onSelect={handleCopyLink}>
					<Icon name="link-2" className="mr-2 h-4 w-4" />
					Copy link
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={handleDownload}>
					<Icon name="download" className="mr-2 h-4 w-4" />
					{downloading ? "Downloading..." : "Download image"}
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<a
						href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						Share on X
					</a>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<a
						href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						Share on WhatsApp
					</a>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
